import type { User, Folder, List, Section, ListItem, AllDataResponse, InheritedCompletion } from '@/types';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (options.body && typeof options.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 204) {
    return {} as T;
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const errorMessage = typeof data === 'object' && data && 'error' in data ? (data as any).error : res.statusText || 'Request failed';
    throw new ApiError(res.status, errorMessage);
  }

  return data as T;
}

export const api = {
  auth: {
    async me(): Promise<User> {
      const { user } = await request<{ user: User }>('/api/auth/me');
      return user;
    },
    loginWithGoogle(returnUrl?: string): void {
      const url = returnUrl
        ? `/api/auth/google/start?return_url=${encodeURIComponent(returnUrl)}`
        : '/api/auth/google/start';
      window.location.href = url;
    },
    async logout(): Promise<void> {
      await request<void>('/api/auth/logout', {
        method: 'POST',
      });
    },
  },

  data: {
    async getAll(): Promise<AllDataResponse> {
      return request<AllDataResponse>('/api/data');
    },
  },

  folders: {
    async list(): Promise<Folder[]> {
      return request<Folder[]>('/api/folders');
    },
    async get(id: string): Promise<Folder> {
      return request<Folder>(`/api/folders/${id}`);
    },
    async create(data: Partial<Folder> & { name: string }): Promise<Folder> {
      return request<Folder>('/api/folders', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    async update(id: string, data: Partial<Folder>): Promise<Folder> {
      return request<Folder>(`/api/folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
    async delete(id: string): Promise<void> {
      return request<void>(`/api/folders/${id}`, {
        method: 'DELETE',
      });
    },
  },

  lists: {
    async list(): Promise<List[]> {
      return request<List[]>('/api/lists');
    },
    async get(id: string): Promise<List> {
      return request<List>(`/api/lists/${id}`);
    },
    async create(data: Partial<List> & { title: string }): Promise<List> {
      return request<List>('/api/lists', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    async update(id: string, data: Partial<List>): Promise<List> {
      return request<List>(`/api/lists/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
    async delete(id: string): Promise<void> {
      return request<void>(`/api/lists/${id}`, {
        method: 'DELETE',
      });
    },
    async setItemsCompleted(id: string, completed: boolean): Promise<ListItem[]> {
      return request<ListItem[]>(`/api/lists/${id}/items/completed`, {
        method: 'PATCH',
        body: JSON.stringify({ completed }),
      });
    },
    async setInheritedItemCompleted(id: string, itemId: string, completed: boolean): Promise<InheritedCompletion> {
      return request<InheritedCompletion>(`/api/lists/${id}/inherited-items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ completed }),
      });
    },
  },

  sections: {
    async list(): Promise<Section[]> {
      return request<Section[]>('/api/sections');
    },
    async get(id: string): Promise<Section> {
      return request<Section>(`/api/sections/${id}`);
    },
    async create(data: Partial<Section> & { list_id: string }): Promise<Section> {
      return request<Section>('/api/sections', {
        method: 'POST',
        body: JSON.stringify({
          list_id: data.list_id,
          title: data.title ?? '',
          position: data.position ?? 0,
        }),
      });
    },
    async update(id: string, data: Partial<Section>): Promise<Section> {
      return request<Section>(`/api/sections/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
    async delete(id: string): Promise<void> {
      return request<void>(`/api/sections/${id}`, {
        method: 'DELETE',
      });
    },
    async setItemsCompleted(id: string, completed: boolean): Promise<ListItem[]> {
      return request<ListItem[]>(`/api/sections/${id}/items/completed`, {
        method: 'PATCH',
        body: JSON.stringify({ completed }),
      });
    },
  },

  items: {
    async list(): Promise<ListItem[]> {
      return request<ListItem[]>('/api/items');
    },
    async get(id: string): Promise<ListItem> {
      return request<ListItem>(`/api/items/${id}`);
    },
    async create(data: Partial<ListItem> & { section_id: string; content: string }): Promise<ListItem> {
      return request<ListItem>('/api/items', {
        method: 'POST',
        body: JSON.stringify({
          section_id: data.section_id,
          content: data.content,
          completed: data.completed ?? false,
          position: data.position ?? 0,
        }),
      });
    },
    async update(id: string, data: Partial<ListItem>): Promise<ListItem> {
      return request<ListItem>(`/api/items/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
    async delete(id: string): Promise<void> {
      return request<void>(`/api/items/${id}`, {
        method: 'DELETE',
      });
    },
  },
};
